/* eslint-disable react/no-unstable-nested-components */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container } from 'src/components/layouts/Container/Container';
import { ButtonToneProps } from 'src/components/ui/Button/Button';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import Icon from 'src/components/ui/Icon/Icon';
import { useWorkflowsQuery, useActivateWorkflow, useArchiveWorkflow, Workflow } from 'src/services/workflows';
import Toggle from 'src/components/ui/Toggle/Toggle';
import { TFunction } from 'i18next';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import { formatDate, formatTime, getActionLabel, getColorForTriggerCategory } from 'src/utils/general';
import { toast } from 'react-toastify';
import { runStatusBadgeType } from './Workflows/WorkflowRuns/WorkflowRuns';
import { TableOverview } from '../components/ui/TableOverview/TableOverview';
import CreateWorkflowOverlay from './Workflows/CreateWorkflowOverlay/CreateWorkflowOverlay';
import { statusTabDomain, WorkflowStatusTab, WORKFLOW_STATUS_TABS } from './Workflows/workflowStatusTabs';

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: TFunction;
}

const tabLabels: Record<WorkflowStatusTab, string> = {
  active: 'workflows.quick_filter.active',
  inactive: 'workflows.quick_filter.inactive',
  all: 'general.all',
};

function WorkflowOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<WorkflowStatusTab>('active');
  const [isCreateOverlayOpen, setIsCreateOverlayOpen] = useState(false);

  const activateWorkflowMutation = useActivateWorkflow();
  const archiveWorkflowMutation = useArchiveWorkflow();

  const useWorkflowsQueryWithSorting = (params?: any) => {
    return useWorkflowsQuery({
      ...params,
      sort_by: 'active',
      sort_order: 'desc',
    });
  };

  const { data: workflows, isLoading } = useWorkflowsQueryWithSorting();

  const handleTabClick = (tab: WorkflowStatusTab) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  };

  const tabs = WORKFLOW_STATUS_TABS.map(tab => ({
    label: t(tabLabels[tab]),
    key: tab,
    onClick: () => handleTabClick(tab),
  }));

  useEffect(() => {
    const currentTab = searchParams.get('tab') as WorkflowStatusTab | null;
    if (currentTab && currentTab !== selectedTab && WORKFLOW_STATUS_TABS.includes(currentTab)) {
      setSelectedTab(currentTab);
    }
  }, [searchParams]);

  const buttons = [
    {
      title: t('workflows.create_new'),
      tone: ButtonToneProps.PRIMARY_BLUE,
      onClick: () => setIsCreateOverlayOpen(true),
    },
  ];

  const badgeLabel: Record<string, string> = {
    'sale.order': t('navbar.outbound'),
    'purchase.order': t('navbar.inbound'),
    'stock.lot': t('navbar.inventory'),
    'stock.quant': t('navbar.inventory'),
    'product.product': t('returns.products'),
    'stock.picking': t('navbar.returns'),
    'df.feed.order': t('navbar.integrations'),
  };

  const handleWorkflowToggle = async (workflowId: number, currentActiveState: boolean) => {
    const newActiveState = !currentActiveState;
    try {
      await activateWorkflowMutation.mutateAsync({
        params: {
          query: { workflow_id: workflowId },
          path: { is_active: newActiveState },
        },
      });
      toast.success(t('success.workflow_activation_updated'));
    } catch (error) {
      console.error('Error activating workflow:', error);
      toast.error(t('errors.workflow_activation_failed'));
    }
  };

  const handleWorkflowArchive = async (workflowId: number) => {
    try {
      await archiveWorkflowMutation.mutateAsync({
        params: {
          query: { workflow_id: workflowId },
        },
      });
      toast.success(t('success.workflow_archived'));
    } catch (error) {
      console.error('Error archiving workflow:', error);
      toast.error(t('errors.workflow_archive_failed'));
    }
  };

  const getColumns = ({ t }: ColumnHelperProps) => [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('general.name'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-x-20 my-6">
          <span className="typo-text-14--bold text-wrap truncate">{info.getValue()}</span>
          <span className="typo-text-12 text-faded text-wrap truncate">{info.row.original.description}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.trigger_name, {
      id: 'trigger',
      size: 160,
      enableHiding: true,
      enableSorting: false,
      enableResizing: false,
      meta: {
        label: t('workflows.trigger_col'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <Badge
            className="max-w-min"
            label={
              info.getValue() || badgeLabel[info.row.original.model?.toLowerCase()] || info.row.original.model || '—'
            }
            expanded
            colorOverride={getColorForTriggerCategory(info.row.original.trigger_category || '')}
          />
        </div>
      ),
    }),
    columnHelper.accessor(row => row.action_types, {
      id: 'action_types',
      size: 120,
      enableHiding: true,
      enableSorting: false,
      enableResizing: false,
      meta: {
        label: t('workflows.actions_col'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <span className="typo-text-12 text-full">
          {(info.getValue() || []).map((type: string) => getActionLabel(type)).join(' · ') || '—'}
        </span>
      ),
    }),
    columnHelper.accessor(row => row.runs?.total, {
      id: 'runs',
      size: 120,
      enableHiding: true,
      enableSorting: false,
      enableResizing: false,
      meta: {
        label: t('workflows.runs_30d'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const { runs } = info.row.original;
        if (!runs || !runs.total) {
          return <span className="typo-text-14 text-faded">{info.row.original.last_execution ? 0 : '—'}</span>;
        }
        return (
          <div className="flex items-center gap-x-8">
            <span className="typo-text-14--bold">{runs.total}</span>
            {runs.failed > 0 && (
              <Badge expanded type={BadgeType.Error} label={`${runs.failed} ${t('workflows.run_status.fail')}`} />
            )}
            {runs.failed === 0 && runs.skipped > 0 && (
              <Badge expanded type={BadgeType.Warning} label={`${runs.skipped} ${t('workflows.run_status.skipped')}`} />
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.created_from, {
      id: 'created_from',
      size: 150,
      enableHiding: true,
      enableSorting: false,
      enableResizing: false,
      meta: {
        label: t('workflows.created_from'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center">
          <span className="typo-text-14">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.create_date, {
      id: 'create_date',
      size: 50,
      enableHiding: true,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('workflows.created_at'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center">
          {info.row.original.create_date ? (
            <div className="flex flex-col gap-y-4">
              <span className="typo-text-14 text-full truncate">{formatDate(info.row.original.create_date)}</span>
              <span className="typo-text-12 text-faded truncate">{formatTime(info.row.original.create_date)}</span>
            </div>
          ) : (
            <span className="typo-text-14 text-full truncate">—</span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor(row => row.last_execution, {
      id: 'last_execution',
      size: 50,
      enableHiding: true,
      enableSorting: false,
      enableResizing: false,
      meta: {
        label: t('workflows.last_execution'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center">
          {info.row.original.last_execution ? (
            <div className="flex flex-col gap-y-4">
              <span className="typo-text-14 text-full truncate">{formatDate(info.row.original.last_execution)}</span>
              <span className="typo-text-12 text-faded truncate flex items-center gap-x-6">
                {formatTime(info.row.original.last_execution)}
                {info.row.original.runs?.last_status && (
                  <Badge
                    showDot
                    dotFilled
                    expanded
                    showBg={false}
                    type={runStatusBadgeType(info.row.original.runs.last_status)}
                    label={t(`workflows.run_status.${info.row.original.runs.last_status}`)}
                    hoverText={t(`workflows.run_status.${info.row.original.runs.last_status}`)}
                  />
                )}
              </span>
            </div>
          ) : (
            <span className="typo-text-12 text-faded truncate">{t('workflows.never_executed')}</span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor(row => row.active, {
      id: 'active',
      size: 50,
      enableHiding: true,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('workflows.status'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        // Stop the click from bubbling to the row, otherwise the row-click
        // action (navigate to the workflow) fires instead of toggling the
        // status. Mirrors the checkbox/actions cells in TableOverview.
        <div role="none" className="flex flex-col gap-y-4" onClick={e => e.stopPropagation()}>
          <Toggle
            checked={info.getValue()}
            onToggle={() => handleWorkflowToggle(info.row.original.id, info.getValue())}
          />
        </div>
      ),
    }),
  ];

  return (
    <Container title={t('navbar.workflows')}>
      <CreateWorkflowOverlay isOpen={isCreateOverlayOpen} onClose={() => setIsCreateOverlayOpen(false)} />
      <HeadlineWithButtons headline={t('navbar.workflows')} buttons={buttons} />
      {!workflows?.items.length && !isLoading ? (
        <div className="flex flex-col justify-center items-center gap-16 h-[430px] w-full rounded-base border-2 border-dashed border-[color:var(--border-distinct)]">
          <span className="text-disabled">
            <Icon name="account-tree" width={48} height={48} />
          </span>
          <span className="typo-title-20 md:typo-title-24 text-disabled text-center">
            {t('workflows.no_workflows_configured')}
          </span>
        </div>
      ) : (
        <TableOverview<Workflow>
          filterModelId="df.workflow"
          actions={[
            {
              title: t('workflows.view_workflow'),
              onExecute: (selected: any) => {
                navigate(`/workflows/${selected[0].id}`);
              },
              icon: 'eye',
              isRowClickAction: true,
              showInContextMenu: false,
              bulkSupport: false,
            },
            {
              title: t('workflows.edit_workflow'),
              onExecute: (selected: any) => {
                navigate(`/workflows/${selected[0].id}?edit=true`);
              },
              icon: 'edit',
              isRowClickAction: false,
              showInContextMenu: true,
              bulkSupport: false,
            },
            {
              title: t('workflows.archive_workflow'),
              onExecute: selectedRows => {
                handleWorkflowArchive(selectedRows[0].id);
              },
              icon: 'delete',
              isError: true,
              isRowClickAction: false,
              showInContextMenu: true,
              bulkSupport: false,
              confirmSettings: {
                renderTitle: () => t('workflows.archive_workflow_title'),
                renderContent: () => t('workflows.archive_workflow_text'),
                confirmLabel: t('general.confirm'),
                cancelLabel: t('general.cancel'),
              },
            },
          ]}
          tabs={tabs}
          getDomainForTab={() => '[]'}
          columns={getColumns({ t })}
          useDataQuery={useWorkflowsQueryWithSorting}
          applyContainer={false}
          extraDomain={statusTabDomain(selectedTab)}
        />
      )}
    </Container>
  );
}

export default WorkflowOverview;
